import React from 'react';
import { useNavigate } from 'react-router-dom';

import { isAuthError } from '../../api/client';
import { clampNumber, getPageSlice, getTotalPages } from '../../domain/common/pagination';
import { canCreateEvent } from '../../domain/events/policy';
import { GroupCard } from '../../features/groups/GroupCard';
import { MemberSelectModal } from '../../features/selection/MemberSelectModal';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { uiActions } from '../../store/slices/uiSlice';
import { fetchGroups, deleteGroup, inviteToGroup, removeFromGroup, confirmGroup, leaveGroup, joinGroup } from '../../store/thunks';
import {
  TavernButton,
  TavernLoading,
  TavernEmptyState,
} from '../../ui/components';
import { TavernSearch } from '../../ui/components/TavernGroups';
import { ConfirmSheet } from '../../ui/ConfirmSheet';

import './GroupsDashboardPage.css';

const GROUPS_PAGE_SIZE = 20;

/**
 * Groups Dashboard Page - Tavern Theme
 * Registry of adventurer parties/guilds
 */
export function GroupsDashboardPage() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const groups = useAppSelector((s) => s.entities.groups.items);
  const role = useAppSelector((s) => s.auth.user?.role);
  const currentUserTelegramId = useAppSelector((s) => s.auth.user?.telegramId);

  const [pageIndex, setPageIndex] = React.useState(0);
  const [isLoading, setIsLoading] = React.useState(false);
  const [hasError, setHasError] = React.useState(false);
  const [isPageSwitching, setIsPageSwitching] = React.useState(false);
  const [searchQuery, setSearchQuery] = React.useState('');
  const [addMembersGroupId, setAddMembersGroupId] = React.useState<string | null>(null);
  const [deleteGroupConfirm, setDeleteGroupConfirm] = React.useState<string | null>(null);
  const [confirmGroupId, setConfirmGroupId] = React.useState<string | null>(null);
  const [leaveGroupId, setLeaveGroupId] = React.useState<string | null>(null);
  const [joinGroupId, setJoinGroupId] = React.useState<string | null>(null);
  const [removeMemberConfirm, setRemoveMemberConfirm] = React.useState<{
    groupId: string;
    telegramId: number;
  } | null>(null);

  const canCreate = canCreateEvent(role);
  const totalPages = getTotalPages(groups.length, GROUPS_PAGE_SIZE);

  React.useEffect(() => {
    setPageIndex((p) => clampNumber(p, 0, totalPages - 1));
  }, [totalPages]);

  const load = React.useCallback(async () => {
    setIsLoading(true);
    setHasError(false);
    try {
      await dispatch(fetchGroups()).unwrap();
    } catch {
      setHasError(true);
    } finally {
      setIsLoading(false);
    }
  }, [dispatch]);

  React.useEffect(() => {
    void load();
  }, [load]);

  // Filter groups by search query
  const filteredGroups = React.useMemo(() => {
    if (!searchQuery) return groups;
    const query = searchQuery.toLowerCase();
    return groups.filter(
      (g) =>
        g.name.toLowerCase().includes(query) ||
        g.description.toLowerCase().includes(query)
    );
  }, [groups, searchQuery]);

  const pageItems = React.useMemo(() => {
    return getPageSlice(filteredGroups, pageIndex, GROUPS_PAGE_SIZE);
  }, [filteredGroups, pageIndex]);

  const canGoBack = pageIndex > 0;
  const canGoForward = pageIndex < totalPages - 1;

  const switchPage = (nextIndex: number) => {
    setIsPageSwitching(true);
    setPageIndex(nextIndex);
    requestAnimationFrame(() => setIsPageSwitching(false));
  };

  const handleDeleteGroup = (groupId: string) => {
    setDeleteGroupConfirm(groupId);
  };

  const confirmDeleteGroup = async () => {
    if (!deleteGroupConfirm) return;
    
    try {
      await dispatch(deleteGroup(deleteGroupConfirm)).unwrap();
      setDeleteGroupConfirm(null);
    } catch (error) {
      if (!isAuthError(error)) {
        dispatch(uiActions.setNotification({ type: 'error', message: 'Не удалось удалить отряд' }));
      }
      setDeleteGroupConfirm(null);
    }
  };

  const handleEditGroup = (groupId: string) => {
    navigate(`/groups/${groupId}/edit`);
  };

  const handleAddMember = async (telegramId: string) => {
    if (!addMembersGroupId) return;

    try {
      await dispatch(
        inviteToGroup({
          groupId: addMembersGroupId,
          telegramIds: [Number(telegramId)],
        })
      ).unwrap();
      setAddMembersGroupId(null);
    } catch (error) {
      if (!isAuthError(error)) {
        dispatch(uiActions.setNotification({ type: 'error', message: 'Не удалось добавить участника' }));
      }
    }
  };

  const currentGroup = addMembersGroupId
    ? groups.find((g) => g.id === addMembersGroupId)
    : null;
  const excludeIds = currentGroup ? currentGroup.members.map((m) => m.telegramId) : [];

  return (
    <div className="groups-dashboard">
      <div className="groups-dashboard-header">
        <h1 className="groups-dashboard-title">
          <span>⚔️</span>
          Реестр отрядов
        </h1>
        {canCreate && (
          <button
            className="groups-create-button"
            onClick={() => navigate('/groups/new')}
          >
            ➕ Создать отряд
          </button>
        )}
      </div>

      {groups.length > 0 && (
        <TavernSearch
          value={searchQuery}
          onChange={setSearchQuery}
          placeholder="Поиск группы по названию или описанию..."
        />
      )}

      <div className="groups-content">
        {isLoading ? (
          <TavernLoading message="Загружаем реестр отрядов..." />
        ) : hasError ? (
          <TavernEmptyState
            icon="⚠️"
            title="Не удалось загрузить группы"
            description="Проверьте подключение и попробуйте снова"
            action={
              <TavernButton variant="secondary" onClick={load}>
                Повторить
              </TavernButton>
            }
          />
        ) : filteredGroups.length === 0 ? (
          <TavernEmptyState
            icon="🛡️"
            title={
              searchQuery
                ? 'Отряды не найдены'
                : 'Пока нет зарегистрированных отрядов'
            }
            description={
              searchQuery
                ? 'Попробуйте изменить поисковый запрос'
                : canCreate
                ? 'Создайте первый отряд для совместных приключений'
                : 'Отряды появятся, когда их создадут другие путешественники'
            }
            action={
              canCreate && !searchQuery ? (
                <TavernButton
                  variant="secondary"
                  onClick={() => navigate('/groups/new')}
                >
                  Создать отряд
                </TavernButton>
              ) : undefined
            }
          />
        ) : isPageSwitching ? (
          <TavernLoading message="Загружаем следующую страницу..." />
        ) : (
          <>
            {pageItems.map((g) => (
              <GroupCard
                key={g.id}
                group={g}
                role={role}
                currentUserTelegramId={currentUserTelegramId}
                onAddMembers={() => setAddMembersGroupId(g.id)}
                onEdit={() => handleEditGroup(g.id)}
                onDelete={() => handleDeleteGroup(g.id)}
                onConfirm={() => setConfirmGroupId(g.id)}
                onLeave={() => setLeaveGroupId(g.id)}
                onJoin={() => setJoinGroupId(g.id)}
                onRemoveMember={(telegramId) =>
                  setRemoveMemberConfirm({ groupId: g.id, telegramId })
                }
              />
            ))}


            {totalPages > 1 && (
              <div className="groups-pagination">
                <TavernButton
                  variant="secondary"
                  disabled={!canGoBack}
                  onClick={() => switchPage(pageIndex - 1)}
                >
                  ← Назад
                </TavernButton>
                <div className="groups-pagination-info">
                  Страница {pageIndex + 1} / {totalPages}
                </div>
                <TavernButton
                  variant="secondary"
                  disabled={!canGoForward}
                  onClick={() => switchPage(pageIndex + 1)}
                >
                  Вперёд →
                </TavernButton>
              </div>
            )}
          </>
        )}
      </div>

      <MemberSelectModal
        isOpen={addMembersGroupId !== null}
        onClose={() => setAddMembersGroupId(null)}
        onSelect={handleAddMember}
        excludeIds={excludeIds}
      />

      <ConfirmSheet
        open={deleteGroupConfirm !== null}
        title="Удалить отряд"
        message="Вы уверены, что хотите удалить этот отряд? Это действие нельзя отменить."
        confirmLabel="Удалить"
        destructive={true}
        onCancel={() => setDeleteGroupConfirm(null)}
        onConfirm={confirmDeleteGroup}
      />

      <ConfirmSheet
        open={removeMemberConfirm !== null}
        title="Удалить участника"
        message="Подтвердить удаление участника из отряда?"
        confirmLabel="Удалить"
        destructive={true}
        onCancel={() => setRemoveMemberConfirm(null)}
        onConfirm={async () => {
          if (!removeMemberConfirm) return;
          try {
            await dispatch(
              removeFromGroup({
                groupId: removeMemberConfirm.groupId,
                telegramIds: [removeMemberConfirm.telegramId],
              })
            ).unwrap();
            setRemoveMemberConfirm(null);
          } catch (err: unknown) {
            if (!isAuthError(err)) {
              const message = err instanceof Error ? err.message : String(err);
              dispatch(uiActions.setNotification({ type: 'error', message: `Ошибка удаления участника: ${message}` }));
            }
            setRemoveMemberConfirm(null);
          }
        }}
      />

      <ConfirmSheet
        open={confirmGroupId !== null}
        title="Подтвердить участие"
        message="Вы хотите подтвердить свое участие в этом отряде?"
        confirmLabel="Подтвердить"
        destructive={false}
        onCancel={() => setConfirmGroupId(null)}
        onConfirm={async () => {
          if (!confirmGroupId) return;
          try {
            await dispatch(confirmGroup(confirmGroupId)).unwrap();
            setConfirmGroupId(null);
          } catch (err: unknown) {
            if (!isAuthError(err)) {
              const message = err instanceof Error ? err.message : String(err);
              dispatch(uiActions.setNotification({ type: 'error', message: `Ошибка подтверждения: ${message}` }));
            }
            setConfirmGroupId(null);
          }
        }}
      />

      <ConfirmSheet
        open={leaveGroupId !== null}
        title="Покинуть отряд"
        message="Вы уверены, что хотите покинуть этот отряд?"
        confirmLabel="Выйти"
        destructive={true}
        onCancel={() => setLeaveGroupId(null)}
        onConfirm={async () => {
          if (!leaveGroupId) return;
          try {
            await dispatch(leaveGroup(leaveGroupId)).unwrap();
            setLeaveGroupId(null);
          } catch (err: unknown) {
            if (!isAuthError(err)) {
              const message = err instanceof Error ? err.message : String(err);
              dispatch(uiActions.setNotification({ type: 'error', message: `Ошибка выхода из отряда: ${message}` }));
            }
            setLeaveGroupId(null);
          }
        }}
      />

      <ConfirmSheet
        open={joinGroupId !== null}
        title="Вступить в отряд"
        message="Вы хотите вступить в этот отряд?"
        confirmLabel="Вступить"
        destructive={false}
        onCancel={() => setJoinGroupId(null)}
        onConfirm={async () => {
          if (!joinGroupId) return;
          try {
            await dispatch(joinGroup(joinGroupId)).unwrap();
            setJoinGroupId(null);
            dispatch(uiActions.setNotification({ type: 'success', message: 'Вы успешно вступили в отряд!' }));
          } catch (err: unknown) {
            if (!isAuthError(err)) {
              const message = err instanceof Error ? err.message : String(err);
              dispatch(uiActions.setNotification({ type: 'error', message: `Ошибка вступления: ${message}` }));
            }
            setJoinGroupId(null);
          }
        }}
      />
    </div>
  );
}

